import { Draft } from 'immer';

import { finleyAssert } from '@finley-technologies/common';

import { PricingOptionModalForm } from './types';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';

export const FIXED_PRICING_OPTION_KEY = 'Fixed';

export const pricingOptionKeyByConvention = (
  pricingOption: PricingOptionModalForm,
) =>
  pricingOption.rateType === 'fixed'
    ? FIXED_PRICING_OPTION_KEY
    : pricingOption.interestRateBenchmark;

export const addPricingOption = (
  newPricingOption: PricingOptionModalForm,
  draft: Draft<CreditAgreementFullArtifact>,
) => {
  if (!draft.loanMechanicsV1) {
    draft.loanMechanicsV1 = {
      pricingOptions: [],
      loans: [],
      commitments: [],
    };
  }

  if (newPricingOption.rateType === 'fixed') {
    finleyAssert(
      !draft.loanMechanicsV1.pricingOptions.some((p) => p.type === 'fixed'),
      'A fixed rate pricing option already exists. Edit the existing one.',
    );

    const fixedPricingOption = {
      type: 'fixed' as const,
      key: pricingOptionKeyByConvention(newPricingOption),
      name: pricingOptionKeyByConvention(newPricingOption),
      value: 0,
      invoiceMechanics: { single: [], recurring: [] },
      margin: {
        type: 'fixed' as const,
        value: 0,
      },
    };
    draft.loanMechanicsV1.pricingOptions.push(fixedPricingOption);
  } else if (newPricingOption.rateType === 'benchmark') {
    finleyAssert(
      !draft.loanMechanicsV1.pricingOptions.some(
        (p) =>
          p.type === 'benchmark' &&
          p.benchmark === newPricingOption.interestRateBenchmark,
      ),
      `A ${newPricingOption.interestRateBenchmark} benchmark pricing option already exists. Edit the existing one.`,
    );

    const benchmarkPricingOption = {
      type: 'benchmark' as const,
      key: pricingOptionKeyByConvention(newPricingOption),
      name: pricingOptionKeyByConvention(newPricingOption),
      value: 0,
      invoiceMechanics: { single: [], recurring: [] },
      benchmark: newPricingOption.interestRateBenchmark,
      adjustments: [],
      change: { type: 'daily' as const },
      determination: { type: 'inAdvance' as const, lookback: { days: 0 } },
      margin: {
        type: 'fixed' as const,
        value: 0,
      },
    };
    draft.loanMechanicsV1.pricingOptions.push(benchmarkPricingOption);
  }
};

export const deletePricingOption = (
  draft: Draft<CreditAgreementFullArtifact>,
  key: string,
) => {
  if (draft?.loanMechanicsV1?.pricingOptions) {
    const pricingOptionHasAssociatedLoan = draft.loanMechanicsV1.loans.find(
      (loan) => loan.pricingOptionKey === key,
    );

    // can only delete pricing option if it is not used in any loan - https://finleytechnologies.slack.com/archives/C076RML97PS/p1744814677560309
    finleyAssert(
      !pricingOptionHasAssociatedLoan,
      `Pricing option [id: ${key}] is used in a loan. We cannot delete a live pricing option.`,
    );

    draft.loanMechanicsV1.pricingOptions = draft.loanMechanicsV1.pricingOptions.filter(
      (liabilityAccount) => liabilityAccount.key !== key,
    );
  }
};
